import { rufe } from './client';
import type { Belegkondition, Herleitungszeile, Preisfuehrung, Summenblock } from './angebote';

/**
 * Der Zugang zum Rechnungstool (Lastenheft Kapitel 8).
 *
 * BETRAEGE SIND ZEICHENKETTEN und DIE SUMMEN KOMMEN VOM SERVER — dieselben
 * beiden Regeln wie bei Angebot und Auftrag (RND-01).
 */
export type { Belegkondition, Herleitungszeile, Preisfuehrung, Summenblock };

export type Rechnungsart =
  | 'rechnung' | 'anzahlungsrechnung' | 'abschlagsrechnung'
  | 'schlussrechnung' | 'storno' | 'gutschrift' | 'dauerrechnung';

export const BELEGARTNAME: Readonly<Record<Rechnungsart, string>> = {
  rechnung: 'Rechnung',
  anzahlungsrechnung: 'Anzahlungsrechnung',
  abschlagsrechnung: 'Abschlagsrechnung',
  schlussrechnung: 'Schlussrechnung',
  storno: 'Storno',
  gutschrift: 'Gutschrift',
  dauerrechnung: 'Dauerrechnung',
};

export type Rechnungsstatus = 'entwurf' | 'festgeschrieben' | 'storniert';

export const STATUSNAME: Readonly<Record<Rechnungsstatus, string>> = {
  entwurf: 'Entwurf',
  festgeschrieben: 'festgeschrieben',
  storniert: 'storniert',
};

export type Zahlungsstand = 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberfaellig' | 'ausgebucht';

export const ZAHLUNGSSTANDNAME: Readonly<Record<Zahlungsstand, string>> = {
  offen: 'offen',
  teilbezahlt: 'teilbezahlt',
  bezahlt: 'bezahlt',
  ueberfaellig: 'überfällig',
  ausgebucht: 'ausgebucht',
};

export type Rechnungspositionsart =
  | 'artikel' | 'freitext' | 'text' | 'zwischensumme' | 'rabatt' | 'anzahlungsabzug';

export type Rechnungsposition = {
  readonly id: string;
  readonly reihenfolge: number;
  readonly abschnitt: number;
  readonly art: Rechnungspositionsart;
  readonly artikel: string | null;
  readonly artikelnummer: string | null;
  readonly bezeichnung: string;
  readonly beschreibung: string | null;
  readonly menge: string | null;
  readonly mengeneinheit: string | null;
  readonly einzelpreis: string | null;
  readonly steuerschluessel: string | null;
  readonly steuerProzent: string | null;
  readonly preisUebersteuert: boolean;
  readonly preisherkunft: string | null;
  readonly rabattProzent: string | null;
  readonly konditionen: readonly Belegkondition[];
  readonly hoechstrabattProzent: string | null;
  readonly herleitung: readonly Herleitungszeile[];
  readonly leistungVon: string | null;
  readonly leistungBis: string | null;
  readonly auftragsposition: string | null;
  readonly anzahlung: string | null;
  readonly zeilenbrutto: string | null;
  readonly netto: string | null;
};

export type Rechnungszeile = {
  readonly id: string;
  readonly rechnungsnummer: string | null;
  readonly belegart: Rechnungsart;
  readonly kunde: string;
  readonly kundenname: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly faelligAm: string;
  readonly status: Rechnungsstatus;
  readonly zahlungsstand: Zahlungsstand;
  readonly belegbrutto: string;
  readonly bezahlterBetrag: string;
  readonly positionen: number;
};

export type Zuschusszeile = {
  readonly gutschein: string;
  readonly rechnungsposition: string;
  readonly bezeichnung: string;
  readonly betrag: string;
};

export type Rechnung = {
  readonly id: string;
  readonly rechnungsnummer: string | null;
  readonly belegart: Rechnungsart;
  readonly kunde: string;
  readonly kundenname: string;
  readonly kundenart: string;
  readonly rechnungsempfaenger: string | null;
  readonly rechnungsempfaengerName: string | null;
  readonly belegdatum: string;
  readonly betreff: string;
  readonly kopftext: string | null;
  readonly fusstext: string | null;
  readonly preisfuehrung: Preisfuehrung;
  readonly zahlungsziel: number;
  readonly skontoProzent: string | null;
  readonly skontoTage: number | null;
  readonly faelligAm: string;
  readonly skontoBis: string | null;
  readonly leitwegId: string | null;
  readonly bestellreferenz: string | null;
  readonly empfaengerName: string | null;
  readonly empfaengerAnschrift: string | null;
  readonly empfaengerUstidnr: string | null;
  /** BT-49: die elektronische Adresse des Unternehmens. */
  readonly empfaengerEmail: string | null;
  /**
   * KUN-08, BUC-103: der Ansprechpartner, an den die Rechnung geht — „z. Hd.“
   * in der Anschrift, Empfänger der E-Mail. Ohne Markierung `null`.
   */
  readonly empfaengerKontaktrolle: string | null;
  readonly empfaengerKontaktName: string | null;
  readonly empfaengerKontaktEmail: string | null;
  readonly leistenderName: string | null;
  readonly leistenderAnschrift: string | null;
  readonly leistenderSteuernummer: string | null;
  readonly leistenderUstidnr: string | null;
  readonly status: Rechnungsstatus;
  readonly festgeschriebenAm: string | null;
  readonly festgeschriebenVonName: string | null;
  readonly bezugsbeleg: string | null;
  readonly bezugsnummer: string | null;
  readonly zahlungsstand: Zahlungsstand;
  readonly bezahlterBetrag: string;
  readonly bezahltAm: string | null;
  readonly ausbuchungsgrund: string | null;
  readonly geloescht: boolean;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly positionen: readonly Rechnungsposition[];
  readonly summen: Summenblock;
  /**
   * PRD-38, PRF-17: angerechnete Gutscheine von Kostenträgern. Sie mindern nur
   * den Zahlbetrag. Im Entwurf: was die Festschreibung anrechnen wird.
   */
  readonly zuschuesse: readonly Zuschusszeile[];
  /** Rechnungsbetrag abzüglich der Zuschüsse — was der Kunde zahlt. */
  readonly zahlbetrag: string;
  /** CRM-174, PRF-17: eingelöste Wertgutscheine — Zahlung, mindern nur den Zahlbetrag. */
  readonly wertgutscheine: readonly { readonly gutschein: string; readonly nummer: string; readonly betrag: string }[];
  /** CRM-169: erfasst, passt auf eine Zeile, aber nach der Festschreibung — nicht angerechnet. */
  readonly zuschuesseNichtAngerechnet: readonly Zuschusszeile[];
  /**
   * AeA-71: vorgemerkte Gutscheine ohne Namen an den Aufträgen dieses Entwurfs
   * — nicht angerechnet, nur ein Hinweis. Nach der Festschreibung leer.
   */
  readonly vormerkungenOffen: readonly {
    readonly auftrag: string;
    readonly auftragsnummer: string;
    readonly kostentraegerName: string;
    readonly betrag: string;
    readonly offen: number;
  }[];
  /** RNG-02: leer heisst festschreibbar. */
  readonly fehlendePflichtangaben: readonly string[];
  readonly korrekturbelege: readonly {
    readonly id: string;
    readonly rechnungsnummer: string | null;
    readonly belegart: Rechnungsart;
    readonly belegdatum: string;
  }[];
  /** AUF-06 (CRM-118): die Aufträge hinter dem Beleg, in ihrer geltenden Fassung. */
  readonly auftraege: readonly { readonly id: string; readonly auftragsnummer: string }[];
};

export type OffenerPosten = {
  readonly rechnung: string;
  readonly rechnungsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly belegdatum: string;
  readonly faelligAm: string;
  readonly tageUeberfaellig: number;
  readonly staffel: string;
  readonly bezahlterBetrag: string;
  readonly zahlungsstand: Zahlungsstand;
  readonly belegart: Rechnungsart;
  readonly betrag: string;
  /** BP-31: Storno und Gutschrift mit Bezug auf diesen Beleg, brutto. */
  readonly gemindert: string;
  /** DAT-12: davon schon erstattet oder verrechnet. */
  readonly erstattet: string;
  /** Der Saldo; negativ ist ein Guthaben des Kunden bis zur Erstattung. */
  readonly offenerBetrag: string;
  readonly art: 'forderung' | 'guthaben';
  readonly ausgleich: string | null;
  /** DAT-12: aus diesem Guthaben mit anderen Forderungen verrechnet. */
  readonly verrechnetAus: string;
  /** DAT-12: auf diese Forderung aus einem Guthaben verrechnet. */
  readonly verrechnetEin: string;
  /** RNG-25: die höchste Mahnstufe aus Navision; null heißt nie gemahnt. */
  readonly mahnstufe: number | null;
  readonly mahndatum: string | null;
  /** RNG-25: die Mahngebühren aller Stufen; gebucht werden sie in Navision. */
  readonly mahngebuehr: string | null;
};

/** RNG-25: eine Mahnung, die Navision erteilt hat (E-39); das CRM zeigt sie nur an. */
export type Navisionmahnung = {
  readonly rechnung: string;
  readonly rechnungsnummer: string | null;
  readonly kunde: string;
  readonly mahnstufe: number;
  readonly mahndatum: string;
  readonly mahngebuehr: string;
  readonly gemeldetAm: string;
};

export type Journalzeile = {
  readonly steuerschluessel: string;
  readonly steuerProzent: string;
  readonly belege: number;
  readonly netto: string;
  readonly steuer: string;
  readonly brutto: string;
};

export type Hausdaten = {
  readonly firmierung: string;
  readonly strasse: string;
  readonly plz: string;
  readonly ort: string;
  readonly land: string;
  readonly steuernummer: string | null;
  readonly ustidnr: string | null;
  readonly telefon: string | null;
  readonly email: string | null;
  readonly webseite: string | null;
  readonly registergericht: string | null;
  readonly registernummer: string | null;
  readonly geschaeftsfuehrung: string | null;
  /** Der Text für den Briefbogen. */
  readonly bankverbindung: string | null;
  /**
   * BT-84 bis BT-86: dieselbe Bankverbindung in Feldern, für die E-Rechnung.
   *
   * Der Text darüber steht auf dem Papier; diese Felder liest der Empfänger
   * maschinell und zahlt danach. Ohne IBAN weist die XRechnung jede
   * Überweisungsrechnung ab (BR-DE-19).
   */
  readonly iban: string | null;
  readonly bic: string | null;
  readonly kontoinhaber: string | null;
};

/* ── Zugriffe ────────────────────────────────────────────────────────────── */

export async function liste(filter: {
  readonly kunde?: string;
  readonly status?: Rechnungsstatus | '';
  readonly zahlungsstand?: Zahlungsstand | '';
  readonly suche?: string;
}): Promise<readonly Rechnungszeile[]> {
  const teile = new URLSearchParams();
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  if (filter.status !== undefined && filter.status !== '') teile.set('status', filter.status);
  if (filter.zahlungsstand !== undefined && filter.zahlungsstand !== '') {
    teile.set('zahlungsstand', filter.zahlungsstand);
  }
  if (filter.suche !== undefined && filter.suche !== '') teile.set('suche', filter.suche);
  const frage = teile.toString();
  return rufe(`/rechnungen${frage === '' ? '' : `?${frage}`}`);
}

export async function akte(id: string): Promise<Rechnung> {
  return rufe(`/rechnungen/${id}`);
}

export async function offenePosten(filter: { readonly stichtag?: string; readonly kunde?: string } = {}):
  Promise<readonly OffenerPosten[]> {
  const teile = new URLSearchParams();
  if (filter.stichtag !== undefined) teile.set('stichtag', filter.stichtag);
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  const frage = teile.toString();
  return rufe(`/rechnungen/offene-posten${frage === '' ? '' : `?${frage}`}`);
}

/** RNG-25: die Mahnungen aus Navision, je Rechnung oder je Kunde, neueste oben. */
export async function mahnungen(filter: { readonly rechnung?: string; readonly kunde?: string }):
  Promise<readonly Navisionmahnung[]> {
  const teile = new URLSearchParams();
  if (filter.rechnung !== undefined) teile.set('rechnung', filter.rechnung);
  if (filter.kunde !== undefined) teile.set('kunde', filter.kunde);
  return rufe(`/rechnungen/mahnungen?${teile.toString()}`);
}

export async function journal(von: string, bis: string): Promise<readonly Journalzeile[]> {
  return rufe(`/rechnungen/journal?von=${von}&bis=${bis}`);
}

export async function nummernluecken(jahr?: number): Promise<{ readonly luecken: readonly number[] }> {
  return rufe(`/rechnungen/nummernluecken${jahr === undefined ? '' : `?jahr=${String(jahr)}`}`);
}

export async function hausdaten(): Promise<Hausdaten | null> {
  return rufe('/rechnungen/hausdaten');
}

export async function hausdatenSetzen(eingabe: Hausdaten): Promise<void> {
  await rufe('/rechnungen/hausdaten', { verfahren: 'PUT', rumpf: eingabe });
}

/** AUF-19, RNG-06: aus einem oder mehreren Aufträgen. */
export async function ausAuftraegen(eingabe: {
  readonly auftraege: readonly string[];
  readonly belegdatum: string;
  readonly betreff?: string | null;
  readonly anzahlungenAbsetzen?: boolean;
  /** RNG-06: nur diese Positionen — die Sammelrechnung aus der Fakturierungsliste. */
  readonly positionen?: readonly string[];
}): Promise<{ readonly id: string }> {
  return rufe('/rechnungen/aus-auftraegen', { verfahren: 'POST', rumpf: eingabe });
}

/** Eine Zeile der Rechnung ohne Angebot — was POST /auftraege/:id/positionen kennt. */
export type Direktzeile = {
  readonly art: 'artikel' | 'freitext';
  readonly artikel: string | null;
  readonly vertriebsform?: string;
  readonly bezeichnung: string | null;
  readonly menge: number;
  /** Bei Artikeln null: Dann gilt der Katalogpreis (PRF-16). */
  readonly einzelpreis: string | null;
  readonly steuerschluessel: string | null;
  /** CRM-174: Nachlass der Zeile — Prozent als Stufe 6, Betrag als eigene Nachlasszeile. */
  readonly rabatt?: Direktrabatt;
  /** CRM-174: Leistungsdatum der Zeile; ohne gilt der Zeitraum der Rechnung. */
  readonly leistungVon?: string;
  readonly leistungBis?: string;
};

export type Direktrabatt =
  | { readonly art: 'prozent'; readonly prozent: string }
  | { readonly art: 'betrag'; readonly betrag: string };

/** CRM-174, PRD-38: Gutschein eines Kostenträgers, an eine Zeile gebunden (Index in `positionen`). */
export type DirektBgGutschein = {
  readonly zeile: number;
  readonly kostentraeger: string;
  readonly nummer: string;
  readonly name: string | null;
  readonly gutscheinwert: string | null;
  readonly betrag: string;
};

/**
 * BUC-75, M-61 (CRM-152): Auftrag anlegen und gleich abrechnen. Der Server legt
 * den Auftrag an, bestätigt ihn und erzeugt den Entwurf in EINER Transaktion.
 */
export async function ausNeuemAuftrag(eingabe: {
  readonly kunde: string;
  readonly betreff: string;
  readonly belegdatum: string;
  readonly bestellreferenz: string | null;
  readonly leistungVon: string;
  readonly leistungBis: string | null;
  readonly positionen: readonly Direktzeile[];
  readonly kopftext?: string;
  readonly fusstext?: string;
  readonly gesamtrabatt?: Direktrabatt;
  /** KUN-08, BUC-103: Ansprechpartner des Kunden als Empfänger („z. Hd.“). */
  readonly empfaengerKontakt?: string;
  readonly bgGutscheine?: readonly DirektBgGutschein[];
  /** GUT-14: beim Anlegen eingelöst, höchstens der offene Betrag. */
  readonly wertgutscheine?: readonly { readonly code: string }[];
}): Promise<{ readonly id: string; readonly auftrag: string; readonly auftragsnummer: string }> {
  return rufe('/rechnungen/aus-neuem-auftrag', { verfahren: 'POST', rumpf: eingabe });
}

/** Die Angaben des Entwurfs, die die Maske „Angaben bearbeiten“ führt (RNG-03, RNG-04). */
export type Kopfangaben = {
  readonly betreff?: string;
  readonly kopftext?: string | null;
  readonly fusstext?: string | null;
  readonly zahlungsziel?: number;
  readonly skontoProzent?: string | null;
  readonly skontoTage?: number | null;
  readonly leitwegId?: string | null;
  readonly bestellreferenz?: string | null;
};

/** Mehrere Kopfangaben in einem Zug; `stand` schützt vor dem Überschreiben (C9.2). */
export async function kopfSichern(
  id: string,
  felder: Kopfangaben,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/rechnungen/${id}`, { verfahren: 'PATCH', rumpf: { stand, ...felder } });
}

/** AUF-16, AUF-20: eine Zeile aus dem Entwurf nehmen; sie bleibt am Auftrag offen. */
export async function positionLoeschen(id: string, position: string): Promise<void> {
  await rufe(`/rechnungen/${id}/positionen/${position}`, { verfahren: 'DELETE' });
}

/**
 * BUC-145, AUF-15: EIN Beleg über alle Stornovorgänge eines Auftrags.
 *
 * Die `hinweise` nennen, was nicht auf dem Beleg steht und von Hand zu
 * ergänzen ist — sie dürfen nicht verschluckt werden.
 */
export async function ausAuftragsstorno(
  auftrag: string,
): Promise<{ readonly id: string; readonly hinweise: readonly string[] }> {
  return rufe(`/rechnungen/aus-auftragsstorno/${auftrag}`, { verfahren: 'POST' });
}

export async function feldSichern(
  id: string,
  feld: string,
  wert: string | number | null,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/rechnungen/${id}`, { verfahren: 'PATCH', rumpf: { stand, [feld]: wert } });
}

/** KUN-08, BUC-103: ein Ansprechpartner, an den die Rechnung gehen kann. */
export type Rechnungskontakt = {
  readonly kontaktrolle: string;
  readonly name: string;
  readonly email: string | null;
  readonly rechnungsempfaenger: boolean;
};

export async function kontaktauswahl(id: string): Promise<readonly Rechnungskontakt[]> {
  return rufe(`/rechnungen/${id}/kontakte`);
}

/** KUN-08, CRM-174: Ansprechpartner eines Kunden, bevor es einen Entwurf gibt. */
export async function kontaktauswahlZumKunden(kunde: string): Promise<readonly Rechnungskontakt[]> {
  return rufe(`/rechnungen/kontaktauswahl?kunde=${encodeURIComponent(kunde)}`);
}

/** KUN-08: den Rechnungsempfänger im Entwurf wechseln; `null` ohne „z. Hd.“. */
export async function empfaengerKontaktSetzen(
  id: string,
  kontaktrolle: string | null,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/rechnungen/${id}/empfaenger-kontakt`, {
    verfahren: 'PUT',
    rumpf: { stand, kontaktrolle },
  });
}

/**
 * KUN-08: an wen die E-Mail mit der Rechnung geht — der Ansprechpartner,
 * sonst die Adresse des Unternehmens (BT-49).
 */
export function versandadresse(r: Rechnung): string | null {
  return r.empfaengerKontaktEmail ?? r.empfaengerEmail;
}

/**
 * Den Leistungszeitpunkt einer Position setzen (§ 14 Abs. 4 Nr. 6, BP-33).
 *
 * `bis` leer heißt „an einem Tag"; `von` leer nimmt den Eintrag zurück. Nur am
 * Entwurf — danach ist der Beleg unveränderlich (RNG-04).
 */
export async function leistungszeitraumSetzen(
  id: string,
  position: string,
  von: string | null,
  bis: string | null,
): Promise<void> {
  await rufe(`/rechnungen/${id}/positionen/${position}/leistungszeitraum`, {
    verfahren: 'PATCH',
    rumpf: { von, bis },
  });
}

/** RNG-04: danach ist der Beleg unveränderlich. */
export async function festschreiben(id: string): Promise<{ readonly rechnungsnummer: string }> {
  return rufe(`/rechnungen/${id}/festschreiben`, { verfahren: 'POST' });
}

/** RNG-05: ein eigener Beleg mit Bezug auf das Original. */
export async function stornieren(
  id: string,
  grund: string,
): Promise<{ readonly id: string; readonly rechnungsnummer: string }> {
  return rufe(`/rechnungen/${id}/storno`, { verfahren: 'POST', rumpf: { grund } });
}

/** RNG-11: Gutschrift mit Bezug als Entwurf — Teilmengen, ein Betrag oder beides. */
export async function gutschriftAnlegen(
  id: string,
  eingabe: {
    readonly grund: string;
    readonly zeilen: readonly { readonly position: string; readonly menge: string }[];
    readonly betrag: { readonly brutto: string; readonly steuerschluessel: string } | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/rechnungen/${id}/gutschrift`, { verfahren: 'POST', rumpf: eingabe });
}

/** RNG-11: Gutschrift ohne Bezug (frei, etwa aus Kulanz) als Entwurf. */
export async function freieGutschriftAnlegen(eingabe: {
  readonly kunde: string;
  readonly grund: string;
  readonly bezeichnung: string;
  readonly brutto: string;
  readonly steuerschluessel: string;
  readonly leistungVon: string;
  readonly leistungBis: string | null;
}): Promise<{ readonly id: string }> {
  return rufe('/rechnungen/gutschrift', { verfahren: 'POST', rumpf: eingabe });
}

export async function zahlungVermerken(
  id: string,
  eingabe: {
    readonly zahlungsstand: Zahlungsstand;
    readonly bezahlterBetrag: string;
    readonly bezahltAm?: string | null;
    readonly ausbuchungsgrund?: string | null;
  },
): Promise<void> {
  await rufe(`/rechnungen/${id}/zahlung`, { verfahren: 'PUT', rumpf: eingabe });
}

/** DAT-12: eine Forderung desselben Kunden, gegen die verrechnet werden kann. */
export type Verrechnungsziel = {
  readonly id: string;
  readonly rechnungsnummer: string | null;
  readonly belegdatum: string;
  readonly faelligAm: string | null;
  readonly offenerBetrag: string;
};

/** DAT-12, FIB-25: eine Erstattung oder Verrechnung am Guthaben, mit Stand der Übertragung. */
export type Guthabenvorgang = {
  readonly art: 'erstattung' | 'verrechnung';
  readonly id: string;
  readonly betrag: string;
  readonly grund: string | null;
  readonly am: string;
  readonly zielrechnungsnummer: string | null;
  readonly mitEingang: boolean | null;
  readonly navZustand: 'nicht_uebertragen' | 'in_uebertragung' | 'uebertragen' | 'fehlerhaft' | null;
  readonly navMeldung: string | null;
};

export type Guthabenstand = {
  readonly posten: string;
  readonly rechnungsnummer: string | null;
  readonly kunde: string;
  readonly rest: string;
  readonly beleg: string | null;
  readonly belegnummer: string | null;
  readonly ziele: readonly Verrechnungsziel[];
  readonly verlauf: readonly Guthabenvorgang[];
};

/** DAT-12: was vom Guthaben am Posten frei ist, wohin es kann, was schon geschah. */
export async function guthaben(posten: string, signal?: AbortSignal): Promise<Guthabenstand> {
  return rufe(`/rechnungen/${posten}/guthaben`, signal === undefined ? {} : { signal });
}

/** DAT-12: erstatten — auch ohne Eingangsvorgang, gegen die bezahlte Rechnung. */
export async function guthabenErstatten(
  posten: string,
  eingabe: { readonly betrag: string; readonly grund: string; readonly idempotenzschluessel: string },
): Promise<{ readonly id: string }> {
  return rufe(`/rechnungen/${posten}/guthaben/erstattung`, { verfahren: 'POST', rumpf: eingabe });
}

/** DAT-12: mit einer anderen offenen Forderung desselben Kunden verrechnen. */
export async function guthabenVerrechnen(
  posten: string,
  eingabe: {
    readonly ziel: string;
    readonly betrag: string;
    readonly grund: string;
    readonly idempotenzschluessel: string;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/rechnungen/${posten}/guthaben/verrechnung`, { verfahren: 'POST', rumpf: eingabe });
}

export async function loeschen(id: string): Promise<void> {
  await rufe(`/rechnungen/${id}`, { verfahren: 'DELETE' });
}
